import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Checkbox, TableRow, TableCell } from '@material-ui/core';
import {
  compose, every, filter, findIndex, get, map,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { FastField, Field } from 'formik';
import { Table } from '../../../common/components';
import { getStyleValue, formatPhoneNumber } from '../../../common/utils';
import messages from '../../messages';

// XXX: 중복 연락처 체크 기능 임시 제거
// const cols = [
//   { id: 'checkbox', style: css` width: 4%;` },
//   { id: 'type', style: css` width: 8%;` },
//   { id: 'group', style: css`width: 12%;` },
//   { id: 'number', style: css`width: 8%;` },
//   { id: 'studentName', style: css`width: 16%;` },
//   { id: 'studentPhoneNumber', style: css`width: 18%;` },
//   { id: 'guardianName', style: css`width: 16%;` },
//   { id: 'guardianPhoneNumber', style: css`width: 18%;` },
// ];
const cols = [
  { id: 'checkbox', style: css` width: 6%;` },
  { id: 'group', style: css`width: 10%;` },
  { id: 'number', style: css`width: 8%;` },
  { id: 'studentName', style: css`width: 18%;` },
  { id: 'studentPhoneNumber', style: css`width: 20%;` },
  { id: 'guardianName', style: css`width: 18%;` },
  { id: 'guardianPhoneNumber', style: css`width: 20%;` },
];

const S = {
  Checkbox: styled(({ classes, dark, ...otherProps }) => (
    <Checkbox
      classes={{
        ...classes,
        checked: 'checked',
      }}
      {...otherProps}
    />
  ))`
    color: ${({ dark, theme }) => theme.palette.grey[dark ? 500 : 400]};
    :hover {
      color: ${({ theme }) => theme.palette.grey[500]};
    }
    &.checked {
      color: ${({ theme }) => theme.palette.primary.light};
    }
    &.Mui-disabled {
      color: ${({ theme }) => theme.palette.grey[300]};
    }
  `,
  Pad: styled.div`
    padding-left: ${({ dense }) => (dense ? '4%' : '20%')};
  `,
  TableRow: styled(({ classes, ...otherProps }) => (
    <TableRow
      classes={{
        ...classes,
        hover: 'hover',
      }}
      {...otherProps}
    />
  ))`
    height: 60px;
    &.hover:hover {
      background-color: ${({ theme }) => theme.palette.blueGrey[25]};
    }
  `,
  TableCell: styled(({ active, ...otherProps }) => <TableCell {...otherProps} />)`
    padding: 0;
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
    :nth-child(1) {
      text-align: center;
    }
    :nth-child(3) {
      font-size: ${({ theme }) => theme.typography.pxToRem(13)};
      text-align: center;
    }
    ${({ variant, theme }) => variant === 'head' && css`
      && {
        color: ${theme.palette.grey[600]};
        font-size: ${theme.typography.pxToRem(13)};
        font-weight: 500;
      }
    `}
  `,
  Input: styled.input`
    width: 60%;
    height: 32px;
    margin: 0 auto 0 24px;
    padding-left: 8px;
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    outline: none;
    :focus, :hover {
      border: 2px solid ${({ error, theme }) => theme.palette[error ? 'error' : 'primary'].main};
    }
    ::-webkit-input-placeholder {
        color: ${({ theme }) => theme.palette.error.main};
    }
    :-moz-placeholder {
      color: ${({ theme }) => theme.palette.error.main};
      opacity: 1;
    }
    ::-moz-placeholder {
      color: ${({ theme }) => theme.palette.error.main};
      opacity: 1;
    }
    :-ms-input-placeholder {
      color: ${({ theme }) => theme.palette.error.main};
    }
    ::placeholder {
      color: ${({ theme }) => theme.palette.error.main};
      opacity: 1;
    }
    ${({ margin }) => margin && css`
      margin-top: ${getStyleValue(margin.top)};
      margin-right: ${getStyleValue(margin.right)};
      margin-bottom: ${getStyleValue(margin.bottom)};
      margin-left: ${getStyleValue(margin.left)};
    `}
    ${({ error, theme }) => error && css`
      border: 2px solid ${theme.palette.error.main};
    `}
  `,
  RequiredNotation: styled.span`
    color: ${({ theme }) => theme.palette.error.main};
  `,
};

const NewContactTable = ({
  contacts, values, setFieldValue,
}) => {
  const getRow = useCallback((contact) => {
    const index = findIndex({ id: contact.id }, contacts);

    return ({
      id: contact.id,
      cells: [
        {
          id: 'checkbox',
          content: (
            <Field name={`contacts.${index}.checked`}>
              {({ field, form, ...otherProps }) => (
                <S.Checkbox
                  color="primary"
                  disabled={!(
                    values.contacts[index].student.name
                    && values.contacts[index].student.grade
                    && values.contacts[index].student.clazzName
                    && values.contacts[index].primaryGuardian.name
                    && values.contacts[index].primaryGuardian.phoneNumber
                  ) || !!get(`contacts[${index}].student.name`, form.errors)
                    || !!get(`contacts[${index}].primaryGuardian.name`, form.errors)
                    || !!get(`contacts[${index}].primaryGuardian.phoneNumber`, form.errors)}
                  checked={field.value}
                  {...field}
                  {...otherProps}
                />
              )}
            </Field>
          ),
        },
        // XXX: 중복 연락처 체크 기능 임시 제거
        // { id: 'type', content: '신규' },
        {
          id: 'group',
          content: contact.student.grade && contact.student.clazzName
            ? `${contact.student.grade}학년 ${contact.student.clazzName}반`
            : '미지정',
        },
        { id: 'number', content: contact.student.clazzNumber },
        {
          id: 'studentName',
          content: contact.student.name
            ? <S.Pad>{contact.student.name}</S.Pad>
            : (
              <FastField name={`contacts.${index}.student.name`}>
                {({ field: { onChange, ...field }, form, ...otherProps }) => {
                  const handleChange = (event) => {
                    onChange(event);

                    if (!event.target.value) {
                      form.setFieldValue(`contacts.${index}.checked`, false);
                    }
                  };

                  return (
                    <S.Input
                      type="text"
                      placeholder="미입력"
                      error={
                        !!(get(field.name, form.errors) && get(field.name, form.touched))
                      }
                      onChange={handleChange}
                      {...field}
                      {...otherProps}
                    />
                  );
                }}
              </FastField>
            ),
        },
        { id: 'studentPhoneNumber', content: formatPhoneNumber(contact.student.phoneNumber) },
        {
          id: 'guardianName',
          content: contact.primaryGuardian.name
            ? <S.Pad>{contact.primaryGuardian.name}</S.Pad>
            : (
              <Field name={`contacts.${index}.primaryGuardian.name`}>
                {({ field: { onChange, ...field }, form }) => {
                  const handleChange = (event) => {
                    onChange(event);

                    if (!event.target.value) {
                      form.setFieldValue(`contacts.${index}.checked`, false);
                    }
                  };

                  return (
                    <S.Input
                      type="text"
                      placeholder="미입력"
                      onChange={handleChange}
                      error={
                        !!(get(field.name, form.errors) && get(field.name, form.touched))
                      }
                      {...field}
                    />
                  );
                }}
              </Field>
            ),
        },
        {
          id: 'guardianPhoneNumber',
          content: contact.primaryGuardian.phoneNumber
            ? <S.Pad dense>{formatPhoneNumber(contact.primaryGuardian.phoneNumber)}</S.Pad>
            : (
              <Field name={`contacts.${index}.primaryGuardian.phoneNumber`}>
                {({ field: { onChange, ...field }, form }) => {
                  const handleChange = (event) => {
                    onChange(event);

                    if (!event.target.value) {
                      form.setFieldValue(`contacts.${index}.checked`, false);
                    }
                  };

                  return (
                    <S.Input
                      type="text"
                      placeholder="미입력"
                      error={
                        !!(get(field.name, form.errors) && get(field.name, form.touched))
                      }
                      margin={{ left: 0 }}
                      onChange={handleChange}
                      {...field}
                    />
                  );
                }}
              </Field>
            ),
        },
      ],
    });
  }, [contacts, values.contacts]);

  const toggleCheckAll = useCallback(() => {
    if (compose(
      every(contact => contact.checked),
      filter(contact => contact.student.name && contact.student.grade && contact.student.clazzName && contact.primaryGuardian.name && contact.primaryGuardian.phoneNumber),
      get('contacts'),
    )(values)) {
      setFieldValue('contacts', map(contact => ({ ...contact, checked: false }), values.contacts));
    } else {
      setFieldValue('contacts', map(contact => ({
        ...contact,
        checked: !!(contact.student.name && contact.student.grade && contact.student.clazzName && contact.primaryGuardian.name && contact.primaryGuardian.phoneNumber),
      }), values.contacts));
    }
  }, [values, setFieldValue]);

  const checkedAll = useMemo(() => compose(
    every(contact => contact.checked),
    filter(contact => contact.student.name && contact.student.grade && contact.student.clazzName && contact.primaryGuardian.name && contact.primaryGuardian.phoneNumber),
    get('contacts'),
  )(values), [values]);
  const head = useMemo(() => ({
    id: 'head',
    cells: [
      {
        id: 'checkbox',
        content: (
          <S.Checkbox
            color="primary"
            checked={checkedAll}
            onChange={toggleCheckAll}
            dark
          />
        ),
      },
      // XXX: 중복 연락처 체크 기능 임시 제거
      // { id: 'type', content: <FormattedMessage {...messages.newContactTableHeadType} /> },
      {
        id: 'group',
        content: (
          <>
            <FormattedMessage {...messages.newContactTableHeadGroup} />
            <S.RequiredNotation>*</S.RequiredNotation>
          </>
        ),
      },
      { id: 'number', content: <FormattedMessage {...messages.newContactTableHeadNumber} /> },
      {
        id: 'studentName',
        content: (
          <S.Pad>
            <FormattedMessage {...messages.newContactTableHeadStudentName} />
            <S.RequiredNotation>*</S.RequiredNotation>
          </S.Pad>
        ),
      },
      { id: 'stduentPhoneNumber', content: <FormattedMessage {...messages.newContactTableHeadStudentPhoneNumber} /> },
      {
        id: 'guardianName',
        content: (
          <S.Pad>
            <FormattedMessage {...messages.newContactTableHeadGuardianName} />
            <S.RequiredNotation>*</S.RequiredNotation>
          </S.Pad>
        ),
      },
      {
        id: 'guardianPhoneNumber',
        content: (
          <S.Pad dense>
            <FormattedMessage {...messages.newContactTableHeadGuardianPhoneNumber} />
            <S.RequiredNotation>*</S.RequiredNotation>
          </S.Pad>
        ),
      },
    ],
  }), [checkedAll, toggleCheckAll]);

  const body = useMemo(() => ({
    rows: map(getRow, contacts),
  }), [contacts, getRow]);

  return (
    <Table
      cols={cols}
      head={head}
      body={body}
      tableRow={S.TableRow}
      tableCell={S.TableCell}
    />
  );
};

NewContactTable.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      number: PropTypes.number,
    }),
  ),
  values: PropTypes.shape({
    contacts: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      student: PropTypes.shape({
        name: PropTypes.string,
        grade: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        clazzName: PropTypes.string,
      }),
      primaryGuardian: PropTypes.shape({
        name: PropTypes.string,
        phoneNumber: PropTypes.string,
      }),
    })),
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

NewContactTable.defaultProps = {
  contacts: null,
};

export default withRouter(NewContactTable);
